import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, AlertTriangle } from 'lucide-react';
import { addChannel, updateChannel, setError } from '../../store/slices/playlistSlice';
import { useTranslation } from 'react-i18next';
import { usePlaylistAutoSave } from '../../hooks/usePlaylistAutoSave';
import type { Channel } from '../../types/index';
import { RootState } from '../../store';
import logger from '../../utils/logger';
import { parseM3U8 } from '../../utils/m3u8Parser';
import { usePlaylistDetection } from '../../contexts/PlaylistDetectionContext';

// Helper to detect if content is a playlist with multiple channels or single stream manifest
const isMultiChannelPlaylist = (content: string): boolean => {
  // Count the number of #EXTINF entries (each represents a channel in a playlist)
  const extinfCount = (content.match(/#EXTINF/g) || []).length;
  
  // Count the number of #EXT-X-STREAM-INF entries (typical in HLS manifests for quality variants)
  const streamInfCount = (content.match(/#EXT-X-STREAM-INF/g) || []).length;
  
  // If there are multiple #EXTINF entries and no/few #EXT-X-STREAM-INF, it's likely a playlist
  return extinfCount > 1 && streamInfCount < extinfCount;
};

interface LinkInputProps {
  editingChannel?: Channel | null;
  onEditComplete?: () => void;
}

export const LinkInput: React.FC<LinkInputProps> = ({ editingChannel, onEditComplete }) => {
  logger.debug('[LinkInput] Component rendering');
  
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { user } = useSelector((state: RootState) => state.auth);
  const { autoSave } = usePlaylistAutoSave();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [playlistDetected, setPlaylistDetected] = useState(false);
  const [httpsChannelsCount, setHttpsChannelsCount] = useState(0);
  const { setPlaylistDetected: setGlobalPlaylistDetected } = usePlaylistDetection();

  useEffect(() => {
    logger.debug('[LinkInput] playlistDetected changed:', playlistDetected);
    setGlobalPlaylistDetected(playlistDetected);
    return () => setGlobalPlaylistDetected(false);
  }, [playlistDetected, setGlobalPlaylistDetected]);

  const [formData, setFormData] = useState({
    name: editingChannel?.name || '',
    url: editingChannel?.url || '',
    logo: editingChannel?.logo || '',
    group: editingChannel?.group || '',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    logger.debug('Form submission started');
    setIsSubmitting(true);
    setPlaylistDetected(false); // Reset playlist detection state

    try {
      logger.debug('Validating URL:', formData.url);
      const urlObj = new URL(formData.url);

      // Fetch the URL content to check if it's a playlist or single stream
      if (!editingChannel) {
        try {
          logger.debug('Fetching URL content to check type:', formData.url);
          const response = await fetch(formData.url, {
            mode: 'cors',
            credentials: 'omit',
            headers: {
              'Accept': 'text/plain, application/x-mpegurl, application/vnd.apple.mpegurl',
            }
          });
          
          logger.debug('Fetch response status:', response.status);
          if (response.ok) {
            const content = await response.text();
            logger.debug('Content received, length:', content.length);
            
            // Check if this is a multi-channel playlist 
            logger.debug('Checking if content is multi-channel playlist');
            if (isMultiChannelPlaylist(content)) {
              logger.debug('Multi-channel playlist detected');
              
              // Parse the playlist
              const parsedChannels = parseM3U8(content);
              
              if (parsedChannels.length > 0) {
                // Show warning and highlight import button
                setHttpsChannelsCount(parsedChannels.length);
                setPlaylistDetected(true);
                setIsSubmitting(false);
                return;
              }
            } else {
              logger.debug('Single stream detected, proceeding with channel add');
            }
          } else {
            logger.warn('Failed to fetch URL content:', response.status);
            // Proceed with single stream add if fetch fails
            logger.debug('Proceeding with single stream add despite fetch failure');
          }
        } catch (error) {
          logger.error('Error checking if URL is a playlist:', error);
          // Proceed with single stream add if fetch fails
          logger.debug('Proceeding with single stream add despite fetch error');
        }
      }

      logger.debug('Preparing channel data');
      const channelData = {
        id: editingChannel ? editingChannel.id : formData.url,
        name: formData.name.trim() || 'Unnamed Channel',
        url: formData.url,
        logo: formData.logo,
        group: formData.group.trim() || 'Uncategorized',
      };

      if (editingChannel) {
        logger.debug('Updating existing channel');
        dispatch(updateChannel(channelData));
        
        // Auto-save changes for both authenticated and non-authenticated users
        try {
          logger.debug('Starting auto-save after channel update');
          await autoSave();
          logger.debug('Auto-save completed successfully');
          dispatch(setError({ 
            message: t('playlist.channelUpdated', 'Channel updated successfully'),
            type: 'success' 
          }));
        } catch (error) {
          logger.error('Failed to auto-save playlist after update:', error);
          dispatch(setError({ 
            message: t('errors.autoSaveFailed', 'Channel updated but failed to save automatically. Please try saving manually.'),
            type: 'error' 
          }));
        }
      } else {
        logger.debug('Adding new channel');
        // Add channel to store
        dispatch(addChannel(channelData));

        // Auto-save for both authenticated and non-authenticated users
        try {
          logger.debug('Starting auto-save after adding channel');
          await autoSave();
          logger.debug('Auto-save completed successfully');
          dispatch(setError({ 
            message: user ? t('playlist.channelAddedAndSaved', 'Channel added and saved successfully') 
                          : t('playlist.channelAdded', 'Channel added successfully'),
            type: 'success' 
          }));
        } catch (error) {
          logger.error('Failed to auto-save playlist after adding channel:', error);
          dispatch(setError({ 
            message: t('errors.autoSaveFailed', 'Channel added but failed to save automatically. Please try saving manually.'),
            type: 'error' 
          }));
        }
      }

      logger.debug('Clearing form data');
      // Clear form
      setFormData({
        name: '',
        url: '',
        logo: '',
        group: '',
      });

      if (onEditComplete) {
        onEditComplete();
      }
    } catch (error) {
      logger.error('Form submission error:', error);
      dispatch(setError({ message: t('errors.invalidUrl', 'Please enter a valid URL'), type: 'error' }));
    } finally {
      if (!playlistDetected) {
        setIsSubmitting(false);
      }
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label htmlFor="url" className="block text-[18px] font-medium text-gray-700 dark:text-gray-300 mb-1">
          {t('playlist.streamUrl', 'Stream URL')}
        </label>
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Link className="h-4 w-4 text-gray-400" />
          </div>
          <input
            type="url"
            name="url"
            id="url"
            value={formData.url}
            onChange={handleInputChange}
            className="w-full pl-10 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary dark:bg-gray-700 dark:text-white"
            placeholder={t('playlist.streamUrl', 'Stream URL')}
            required
          />
        </div>
        
        {/* Playlist Detection Warning */}
        {playlistDetected && (
          <div className="mt-2 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-md">
            <div className="flex items-start">
              <AlertTriangle className="h-5 w-5 text-amber-500 dark:text-amber-400 mt-0.5" />
              <div className="ml-3">
                <p className="text-sm text-amber-800 dark:text-amber-200">
                  {t('playlist.playlistWarning', { count: httpsChannelsCount })}
                </p>
                <p className="mt-1 text-sm text-amber-700 dark:text-amber-300">
                  {t('playlist.useImportPlaylist')}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
      
      <div>
        <label htmlFor="name" className="block text-[18px] font-medium text-gray-700 dark:text-gray-300 mb-1">
          {t('playlist.channelName', 'Channel Name')}
        </label>
        <input
          type="text"
          name="name"
          id="name"
          value={formData.name}
          onChange={handleInputChange}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary dark:bg-gray-700 dark:text-white"
          placeholder={t('playlist.channelName', 'Channel Name')}
        />
      </div>
      
      <div>
        <label htmlFor="group" className="block text-[18px] font-medium text-gray-700 dark:text-gray-300 mb-1">
          {t('playlist.group', 'Group (Optional)')}
        </label>
        <input
          type="text"
          name="group"
          id="group"
          value={formData.group}
          onChange={handleInputChange}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary dark:bg-gray-700 dark:text-white"
          placeholder={t('playlist.group', 'Group (Optional)')}
        />
      </div>
      
      <div>
        <label htmlFor="logo" className="block text-[18px] font-medium text-gray-700 dark:text-gray-300 mb-1">
          {t('playlist.logoUrl', 'Logo URL')}
        </label>
        <input
          type="url"
          name="logo"
          id="logo"
          value={formData.logo}
          onChange={handleInputChange}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary dark:bg-gray-700 dark:text-white"
          placeholder={t('playlist.logoUrlPlaceholder', 'https://example.com/logo.png')}
        />
      </div>
      
      <div className="mt-4">
        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full py-2 px-4 flex justify-center items-center border border-transparent rounded-md shadow-sm text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
        >
          {isSubmitting ? (
            <>
              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              {t('common.saving', 'Saving...')}
            </>
          ) : editingChannel ? (
            t('playlist.updateChannel', 'Update Channel')
          ) : (
            t('playlist.addChannel', 'Add Channel')
          )}
        </button>
      </div>
    </form>
  );
};
